import { create } from "zustand";

import type { EnemyType, OdbKind, OdbSide } from "../../api/types";

/** Création demandée à l'ouverture de la vue (bouton des Données initiales) : le dialogue « Nouvel ordre de
 *  bataille » s'ouvre prérempli (camp, nature du niveau de l'ordre, départ). */
export interface OrbatsCreate {
  side: OdbSide;
  kind: OdbKind;
  /** Départ proposé : structure type (ennemi : structure ternaire générique), racine seule, ou copie d'un ordre de
   *  bataille fourni (amis). */
  start?: "structure" | "racine" | "copie";
}

/** Dossier d'où vient la demande : après création ou enregistrement, l'éditeur propose « Employer dans le dossier »
 *  pour ce champ des Données initiales. */
export interface OrbatsOrigin {
  exerciseId: string | null;
  exerciseName: string | null;
  field: "odb" | "eni_odb";
  /** Niveau de l'ordre du dossier (une force de niveau régiment est « ODB/racine », de niveau compagnie
   *  « ODB/racine/S-GTIA »). */
  level: "REGIMENT" | "BRIGADE" | "DIVISION" | "CORPS" | "COMPAGNIE";
  /** Faux : le dossier d'origine affronte l'ennemi générique (force non française, registre B39) — l'éditeur y
   *  cache les ennemis des unités françaises et n'y propose que des ordres de bataille ennemis génériques. */
  referenceEnemy?: boolean;
}

/** Choix à reporter dans le brouillon des Données initiales (rien n'est enregistré d'office). */
export interface OrbatsUse {
  exerciseId: string | null;
  field: "odb" | "eni_odb";
  value: string;
  /** Ennemi : faction d'un ordre de bataille qui en porte une, pour le champ « Ennemi » (null : ennemi générique,
   *  le champ garde son choix). */
  enemyType?: EnemyType | null;
  at: number;
}

/** Ordre de bataille à montrer à l'ouverture de la vue Ordres de bataille : camp (Amis / Ennemis) et ordre de
 *  bataille à sélectionner (null : le dernier modifié), création à préparer et dossier d'origine. Posé par les liens
 *  des Données initiales, lu par la vue à son ouverture. `at` distingue deux demandes identiques. `use` : choix
 *  « Employer dans le dossier », relu par les Données initiales à leur ouverture. */
interface OrbatsTarget {
  side: OdbSide;
  code: string | null;
  at: number;
  create: OrbatsCreate | null;
  origin: OrbatsOrigin | null;
  use: OrbatsUse | null;
  open: (side: OdbSide, code?: string | null, options?: { create?: OrbatsCreate | null; origin?: OrbatsOrigin | null }) => void;
  employ: (use: Omit<OrbatsUse, "at">) => void;
  consumeUse: () => void;
}

export const useOrbatsTarget = create<OrbatsTarget>((set) => ({
  side: "AMI",
  code: null,
  at: 0,
  create: null,
  origin: null,
  use: null,
  open: (side, code = null, options = {}) =>
    set({ side, code, at: Date.now(), create: options.create ?? null, origin: options.origin ?? null }),
  employ: (use) => set({ use: { ...use, at: Date.now() }, origin: null }),
  consumeUse: () => set({ use: null }),
}));
